import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthStore } from '@auth/data-access';
import { APP_CONFIG, EXTERNAL_APIS, hasPlatformFeature, PLATFORM_FEATURES } from '@application/config';
import {
  EdgeDeviceResponse,
  IpifyResponse,
  CreateAccessPointRequest,
  UpdateAccessPointRequest,
  AccessPointResponse,
  AccessPointListItem,
  ELogicalDirectionEnum,
  EvaluateByPersonIdRequest,
  EvaluateByPersonIdResponse,
  IEdgeEventResponse,
  IEdgeEventsGetParams,
  SimulateAccessResponse,
  ISpaceAccessPoint,
  BiometricReader,
  EEdgeDeviceAction,
} from '../entities/access-edges.interface';
import { EdgeConnectionReport } from '../entities/edge-connection-report.model';

@Injectable({ providedIn: 'root' })
export class AccessPointEdgeService {
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);
  private readonly authStore = inject(AuthStore);
  private readonly apiBase = this.appConfig.apiUrl.access_edge_manager_service;
  private readonly accessDirectoryApi = this.appConfig.apiUrl.access_directory_service;
  private readonly accessServiceApi = this.appConfig.apiUrl.access_service;

  public getPublicIp(): Observable<IpifyResponse> {
    return this.http.get<IpifyResponse>('https://api.ipify.org?format=json');
  }

  public getPublicIpFallback(): Observable<{ ip: string }> {
    return this.http.get<{ ip: string }>(EXTERNAL_APIS.ipApi);
  }

  public loadInitialDevices(): Observable<EdgeDeviceResponse[]> {
    return this.http.get<EdgeDeviceResponse[]>(
      `${this.apiBase}/v1/edge-manager`,
      { withCredentials: true }
    );
  }

  public bindEdge(edgeId: string): Observable<string> {
    return this.http.post(
      `${this.accessDirectoryApi}/BindEdge/${encodeURIComponent(edgeId)}`,
      {},
      { responseType: 'text', withCredentials: true }
    );
  }

  public unbindEdge(edgeId: string): Observable<string> {
    return this.http.post(
      `${this.accessDirectoryApi}/UnbindEdge/${encodeURIComponent(edgeId)}`,
      {},
      { responseType: 'text', withCredentials: true }
    );
  }

  /**
   * Marca todas as digitais embarcadas daquele equipamento como não
   * sincronizadas, forçando a propagação a reenviar tudo.
   *
   * **Não apaga nada** — é manutenção, não parte do fluxo de cadastro, e por
   * isso vive na tela de equipamentos e não no modal da pessoa. Pedir
   * confirmação antes de chamar.
   */
  public resetDeviceByUser(macAddress: string): Observable<void> {
    return this.http.delete<void>(
      `${this.accessDirectoryApi}/EmbeddedTemplate/ResetDeviceByUser`,
      { params: { macAddress }, withCredentials: true }
    );
  }

  public getByEdgeId(edgeId: string): Observable<any> {
    return this.http.get<any>(
      `${this.accessDirectoryApi}/Equipment/GetByEdgeId`,
      { 
        params: {
          edgeId
        },
        withCredentials: true }
    );
  }

  /**
   * Lista os pontos de acesso do inquilino (resolvido pelo token — não recebe
   * `tenantId`).
   *
   * `OData/v2` devolve array nu com o edge já achatado no item, dispensando o
   * `$expand=EdgeConfigurationDeployment` que a v1 exigia só para desenterrar o
   * `EdgeId` — e que trazia junto o `ConfigurationJson` de cada deployment.
   */
  /**
   * O ponto de acesso ligado a um edge, para a tela poder chamá-lo pelo nome que
   * o utilizador deu em vez do identificador técnico.
   *
   * O `ConnectionReport` só devolve `edgeId` — nome é assunto do directory, não
   * do event log. Filtra em vez de trazer a lista toda: a tela precisa de uma
   * linha, e a lista cresce com o número de catracas do inquilino.
   *
   * Devolve array porque é o que o OData devolve; vazio quando o edge ainda não
   * tem ponto configurado, que é um estado normal e não um erro.
   */
  public getAccessPointByEdgeId(edgeId: string): Observable<AccessPointListItem[]> {
    const params = new HttpParams().set('$filter', `EdgeId eq '${edgeId}'`);

    return this.http.get<AccessPointListItem[]>(
      `${this.accessDirectoryApi}/OData/v2/AccessPoints`,
      { params, withCredentials: true },
    );
  }

  public getAccessPoints(): Observable<AccessPointListItem[]> {
    return this.http.get<AccessPointListItem[]>(
      `${this.accessDirectoryApi}/OData/v2/AccessPoints`,
      { withCredentials: true }
    );
  }

  public createAccessPoint(data: CreateAccessPointRequest): Observable<AccessPointResponse> {
    return this.http.post<AccessPointResponse>(
      `${this.accessDirectoryApi}/AccessPoint`,
      data,
      { withCredentials: true }
    );
  }

  public updateAccessPoint(data: UpdateAccessPointRequest): Observable<AccessPointResponse> {
    return this.http.patch<AccessPointResponse>(
      `${this.accessDirectoryApi}/AccessPoint`,
      data,
      { withCredentials: true }
    );
  }

  public loadBiometricReaders(edgeId: string): Observable<BiometricReader[]> {
    return this.http.post<BiometricReader[]>(
      `${this.apiBase}/v1/edge-command/${encodeURIComponent(edgeId)}/get-biometric-readers`,
      {},
      { withCredentials: true }
    );
  }

  public getAccessPointById(accessPointId: string): Observable<AccessPointResponse> {
    return this.http.get<AccessPointResponse>(
      `${this.accessDirectoryApi}/AccessPoint/${encodeURIComponent(accessPointId)}`,
      { withCredentials: true }
    );
  }

  public createSpaceAccessPoint(data: { AccessPointId: string; SpaceId: string }): Observable<ISpaceAccessPoint> {
    return this.http.post<ISpaceAccessPoint>(
      `${this.accessServiceApi}/SpacesAccessPoints`,
      data,
      { withCredentials: true }
    );
  }

  public deleteSpaceAccessPoint(accessPointId: string, spaceId: string): Observable<void> {
    const params = new HttpParams()
      .set('accessPointId', accessPointId)
      .set('spaceId', spaceId);
    return this.http.delete<void>(
      `${this.accessServiceApi}/SpacesAccessPoints/Delete`,
      { params, withCredentials: true }
    );
  }

  public deleteAccessPoint(accessPointId: string): Observable<string> {
    const params = new HttpParams().set('id', accessPointId);
    return this.http.delete(
      `${this.accessDirectoryApi}/AccessPoint`,
      { params, responseType: 'text', withCredentials: true }
    );
  }

  public releaseEdge(edgeId: string, direction: ELogicalDirectionEnum): Observable<string> {
    return this.http.post(
      `${this.apiBase}/v1/edge-command/${encodeURIComponent(edgeId)}/release/${direction}`,
      {},
      { responseType: 'text', withCredentials: true }
    );
  }

  public evaluateByPersonId(
    edgeId: string,
    request: EvaluateByPersonIdRequest
  ): Observable<EvaluateByPersonIdResponse> {
    return this.http.post<EvaluateByPersonIdResponse>(
      `${this.apiBase}/AccessEvaluation/EvaluateByPersonId?edgeId=${encodeURIComponent(edgeId)}`,
      request,
      { withCredentials: true }
    );
  }

  /**
   * Simulates an access evaluation for a person.
   * GET AccessEvaluation/SimulateAccess?personId=... — served by accessservice-api,
   * or by schoolservice-api on school-platform tenants (same path and response shape).
   * Sends Bearer token via Authorization header (and withCredentials for interceptor fallback).
   */
  public simulateAccess(personId: string): Observable<SimulateAccessResponse> {
    const token = this.authStore.getAuthData()?.Token;
    const headers = token
      ? new HttpHeaders({ Authorization: `Bearer ${token}` })
      : new HttpHeaders();
    const isSchoolPlatform = hasPlatformFeature(
      this.appConfig.enabledPlatformFeatures,
      PLATFORM_FEATURES.school
    );
    const baseUrl = isSchoolPlatform ? this.appConfig.apiUrl.school_service : this.accessServiceApi;
    return this.http.get<SimulateAccessResponse>(
      `${baseUrl}/AccessEvaluation/SimulateAccess`,
      { params: { personId }, headers, withCredentials: true }
    );
  }

  public getWebcams(edgeId: string): Observable<Array<{ Index: number; InstanceId: string; Name: string }>> {
    return this.http.post<Array<{ Index: number; InstanceId: string; Name: string }>>(
      `${this.apiBase}/v1/edge-command/${encodeURIComponent(edgeId)}/get-webcams`,
      {},
      { withCredentials: true }
    );
  }

  /**
   * Liga/desliga as câmeras de um ponto de acesso no edge, para o browser poder usá-las.
   *
   * O parâmetro é `actions` (plural) — o controller faz binding por esse nome exato e, se
   * ele não chegar, assume `Activate` em silêncio (é o membro 0 do enum). Ou seja, um nome
   * errado não dá erro: vira "ligue a câmera", o oposto do pretendido.
   *
   * O comando vale para o edge inteiro. A API não recebe nome de dispositivo e o agente
   * para todos os slots de câmera do ponto de acesso.
   */
  public deviceAction(edgeId: string, action: EEdgeDeviceAction): Observable<void> {
    return this.http.post<void>(
      `${this.apiBase}/v1/edge-command/${encodeURIComponent(edgeId)}/device-action`,
      null,
      {
        params: { actions: action },
        withCredentials: true
      }
    );
  }

  public captureSnapshot(edgeId: string): Observable<string | number[]> {
    return this.http.post<string | number[]>(
      `${this.apiBase}/v1/edge-command/${encodeURIComponent(edgeId)}/snapshot`,
      null,
      { responseType: 'json', withCredentials: true }
    );
  }

  /**
   * Override a denied access decision so the person can be allowed through.
   * POST /v1/edge-command/{edgeId}/override-denied/{attemptId}
   */
  public overrideDenied(edgeId: string, attemptId: string, reason?: string): Observable<void> {
    return this.http.post<void>(
      `${this.apiBase}/v1/edge-command/${encodeURIComponent(edgeId)}/override-denied/${encodeURIComponent(attemptId)}`,
      reason ? { reason } : {},
      { withCredentials: true }
    );
  }

  public getEdgeEvents(params?: IEdgeEventsGetParams): Observable<IEdgeEventResponse[]> {
    let httpParams = new HttpParams();
    if (params?.take != null) {
      httpParams = httpParams.set('take', String(params.take));
    }
    if (params?.edgeId != null) {
      httpParams = httpParams.set('edgeId', params.edgeId);
    }
    if (params?.beforeId != null) {
      httpParams = httpParams.set('beforeId', params.beforeId);
    }
    return this.http.get<IEdgeEventResponse[]>(`${this.apiBase}/EdgeEventLog/Get`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  /**
   * Relatório de saúde de conexão do edge — degradações, quedas e passagens na
   * mesma grade dia×hora, mais as amostras de RTT.
   *
   * `timeZone` é o fuso do ESTABELECIMENTO, não o do browser: o servidor monta
   * as grades, o `summary` e o `dropsAt` nele. Quem chama passa
   * `DateTimeService.getUserZone()`, que lê o cadastro do inquilino.
   *
   * Edge sem eventos responde `200` com `days: []` e totais zerados — é estado
   * vazio, não erro.
   *
   * GET /EdgeEventLog/ConnectionReport?edgeId={edgeId}&timeZone={iana}
   */
  public getConnectionReport(
    edgeId: string,
    timeZone: string,
    period?: { from: string; to: string },
  ): Observable<EdgeConnectionReport> {
    let params = new HttpParams().set('edgeId', edgeId).set('timeZone', timeZone);

    if (period) {
      // Dias LOCAIS do inquilino, `yyyy-MM-dd`, e vão juntos ou não vão — o
      // servidor devolve `400` se só um deles vier. Os nomes não terminam em
      // `Utc` e não estão declarados em `UTC_DATE_FIELDS`, portanto o
      // `DateInterceptor` deixa-os passar intactos, que é o que se quer: são
      // dias de calendário, não instantes.
      params = params.set('from', period.from).set('to', period.to);
    }

    return this.http.get<EdgeConnectionReport>(
      `${this.apiBase}/EdgeEventLog/ConnectionReport`,
      { params, withCredentials: true },
    );
  }
}
